import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, Box, Typography, IconButton, Stack, Button, TextField,
} from '@mui/material';
import { Close, Add, Remove } from '@mui/icons-material';
import type { MenuItem } from '../types';
import { useLang, pickLocale, pickDescLocale } from '../i18n/LanguageProvider';
import { palette } from '../theme';

interface Props {
  open: boolean;
  item: MenuItem | null;
  /** Category name for the Unsplash fallback when item.image_url is missing/broken. */
  categoryName?: string;
  onClose: () => void;
  onAdd(item: MenuItem, qty: number, notes: string): void;
}

const fmt = (n: number) => `${n.toFixed(2).replace('.', ',')} EUR`;

// Same map as MenuItemCard — keep them in sync.
const CATEGORY_IMAGES: Record<string, string> = {
  'crispy rolls':           'https://images.unsplash.com/photo-1617196034796-73dfa7b1fd56',
  'inside out':             'https://images.unsplash.com/photo-1617196701537-7329482cc9fe',
  'inside-out special':     'https://images.unsplash.com/photo-1611143669185-af224c5e3252',
  'maki':                   'https://images.unsplash.com/photo-1611143669185-af224c5e3252',
  'nigiri':                 'https://images.unsplash.com/photo-1564489563601-c53cfc451e93',
  'sashimi':                'https://images.unsplash.com/photo-1534482421-64566f976cfa',
  'poke bowls':             'https://images.unsplash.com/photo-1546069901-ba9599a7e63c',
  'sushi box':              'https://images.unsplash.com/photo-1583623025817-d180a2221d0a',
  'burgers':                'https://images.unsplash.com/photo-1568901346375-23c9450c58cd',
  'pizza':                  'https://images.unsplash.com/photo-1574071318508-1cdbab80d002',
  'pizza star':             'https://images.unsplash.com/photo-1565299624946-b28f40a0ae38',
  'pizza starter':          'https://images.unsplash.com/photo-1565299624946-b28f40a0ae38',
  'pasta':                  'https://images.unsplash.com/photo-1473093295043-cdd812d0e601',
  'shawarma':               'https://images.unsplash.com/photo-1561651823-34feb02250e4',
  'soups':                  'https://images.unsplash.com/photo-1547592180-85f173990554',
  'starters':               'https://images.unsplash.com/photo-1541544741938-0af808871cc0',
  'salads':                 'https://images.unsplash.com/photo-1512621776951-a57141f2eefd',
  'desserts':               'https://images.unsplash.com/photo-1551024506-0bccd828d307',
  'desserts & daifuku mochis': 'https://images.unsplash.com/photo-1607478900766-efe13248b125',
  'beverages':              'https://images.unsplash.com/photo-1544145945-f90425340c7e',
  'drinks':                 'https://images.unsplash.com/photo-1544145945-f90425340c7e',
  'extras':                 'https://images.unsplash.com/photo-1565958011703-44f9829ba187',
  'extra dips':             'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136',
};
const DEFAULT_FALLBACK = 'https://images.unsplash.com/photo-1504674900247-0877df9cc836';
function categoryImage(category?: string): string {
  const key = (category || '').toLowerCase().trim();
  return `${CATEGORY_IMAGES[key] || DEFAULT_FALLBACK}?w=900&auto=format&q=75`;
}

export const ItemDetailModal: React.FC<Props> = ({ open, item, categoryName, onClose, onAdd }) => {
  const { lang, t } = useLang();
  const [qty, setQty] = useState(1);
  const [notes, setNotes] = useState('');
  // 0 = primary url, 1 = category Unsplash, 2 = emoji placeholder
  const [stage, setStage] = useState<0 | 1 | 2>(0);

  useEffect(() => {
    if (open) {
      setQty(1);
      setNotes('');
      setStage(item?.image_url ? 0 : 1);
    }
  }, [open, item?.id, item?.image_url]);

  if (!item) return null;
  const name = pickLocale(item, lang);
  const desc = pickDescLocale(item, lang);
  const total = (Number(item.base_price) || 0) * qty;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          background: `linear-gradient(180deg, ${palette.bgElev} 0%, ${palette.bg} 100%)`,
          color: '#fff',
          borderRadius: { xs: 0, sm: 3 },
          border: `1px solid ${palette.border}`,
          overflow: 'hidden',
        },
      }}
    >
      <Box sx={{ position: 'relative' }}>
        {stage === 0 && item.image_url && (
          <Box
            component="img"
            src={item.image_url}
            alt={name}
            onError={() => setStage(1)}
            sx={{ width: '100%', height: { xs: 220, sm: 280 }, objectFit: 'cover', display: 'block', backgroundColor: palette.bgElev }}
          />
        )}
        {stage === 1 && (
          <Box
            component="img"
            src={categoryImage(categoryName)}
            alt={name}
            onError={() => setStage(2)}
            sx={{ width: '100%', height: { xs: 220, sm: 280 }, objectFit: 'cover', display: 'block', backgroundColor: palette.bgElev }}
          />
        )}
        {stage === 2 && (
          <Box
            sx={{
              width: '100%',
              height: { xs: 180, sm: 240 },
              background: `linear-gradient(135deg, ${palette.bgElev} 0%, ${palette.bg} 100%)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '64px',
              color: `${palette.primary}88`,
            }}
          >
            🍽️
          </Box>
        )}
        <IconButton
          onClick={onClose}
          aria-label="close"
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            backgroundColor: 'rgba(0,0,0,0.5)',
            color: '#fff',
            backdropFilter: 'blur(10px)',
            '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' },
          }}
        >
          <Close />
        </IconButton>
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: `linear-gradient(180deg, transparent 50%, ${palette.bgElev} 100%)`,
          }}
        />
      </Box>

      <DialogContent sx={{ p: { xs: 2, sm: 3 }, pt: { xs: 2, sm: 2 } }}>
        <Typography dir="auto" sx={{ color: '#fff', fontWeight: 800, fontSize: '24px', mb: 0.5, textAlign: 'start', unicodeBidi: 'isolate' }}>
          {name}
        </Typography>
        {desc && (
          <Typography dir="auto" sx={{ color: palette.textMuted, mb: 2, fontSize: '14px', lineHeight: 1.5, textAlign: 'start', unicodeBidi: 'isolate' }}>
            {desc}
          </Typography>
        )}

        <Box sx={{ mb: 2 }}>
          <Typography sx={{ color: palette.textMuted, fontSize: '12px', mb: 1, letterSpacing: '1px', textTransform: 'uppercase' }}>
            {t('item.notes')}
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={2}
            placeholder={t('item.notes_placeholder')}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            sx={inputSx}
          />
        </Box>

        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <IconButton
              onClick={() => setQty((q) => Math.max(1, q - 1))}
              sx={{ color: palette.primary, background: 'rgba(0,200,150,0.1)', '&:hover': { background: 'rgba(0,200,150,0.2)' } }}
            >
              <Remove />
            </IconButton>
            <Typography sx={{ minWidth: 32, textAlign: 'center', fontWeight: 700, fontSize: '20px' }}>
              {qty}
            </Typography>
            <IconButton
              onClick={() => setQty((q) => q + 1)}
              sx={{ color: palette.primary, background: 'rgba(0,200,150,0.1)', '&:hover': { background: 'rgba(0,200,150,0.2)' } }}
            >
              <Add />
            </IconButton>
          </Stack>
          <Typography sx={{ color: palette.primary, fontWeight: 800, fontSize: '22px' }}>
            {fmt(total)}
          </Typography>
        </Stack>

        <Button
          fullWidth
          variant="contained"
          size="large"
          onClick={() => { onAdd(item, qty, notes); onClose(); }}
          sx={{
            py: 1.5,
            fontWeight: 700,
            fontSize: '15px',
            background: `linear-gradient(135deg, ${palette.primary} 0%, ${palette.primaryDark} 100%)`,
            color: palette.bg,
            '&:hover': {
              background: `linear-gradient(135deg, ${palette.primaryHoverHi} 0%, ${palette.primaryHoverLo} 100%)`,
              boxShadow: `0 8px 24px ${palette.primary}55`,
            },
          }}
        >
          {t('item.add_to_cart')} · {fmt(total)}
        </Button>
      </DialogContent>
    </Dialog>
  );
};

const inputSx = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'rgba(255,255,255,0.05)',
    color: '#fff',
    '& fieldset': { borderColor: 'rgba(0,200,150,0.2)' },
    '&:hover fieldset': { borderColor: 'rgba(0,200,150,0.4)' },
    '&.Mui-focused fieldset': { borderColor: '#00C896' },
  },
  '& .MuiInputBase-input::placeholder': {
    color: 'rgba(255,255,255,0.4)',
    opacity: 1,
  },
};
